// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Pure presentational analysis-result view — no behavior change.
import React from 'react';

// ───────────────────────────────────────────────────────────────
//   QUALITATIVE / DESCRIPTOR (DUS) ANALYSIS VIEW  (Jul 2026)
//   Categorical descriptor analysis: frequency + diversity indices,
//   Gower dendrogram, DUS distinctness matrix. Deterministic
//   interpretations are echoed straight from the backend numbers.
// ───────────────────────────────────────────────────────────────
const _QDUS_CLASS_STYLE = {
  monomorphic: { bg: '#FEF2F2', border: '#DC2626', text: '#991B1B', label: 'Monomorphic' },
  near_monomorphic: { bg: '#FFFBEB', border: '#D97706', text: '#92400E', label: 'Near-monomorphic' },
  polymorphic: { bg: '#F0FDF4', border: '#16A34A', text: '#166534', label: 'Polymorphic' },
};

export default function QualitativeDUSView({ data }) {
  if (!data || data.error) {
    return <div className="p-4 text-red-700" data-testid="qualitative-dus-error">{data?.error || 'No data'}</div>;
  }
  const ds = data.design_summary || {};
  const exec = data.executive_summary || {};
  const gower = data.gower || {};
  const dist = data.distinctness || {};
  const diversity = data.diversity_summary || [];
  const traits = data.traits || [];

  return (
    <div className="space-y-6" data-testid="qualitative-dus-view">
      {/* Executive summary */}
      <div className="bg-gradient-to-br from-[#ECFEFF] to-[#F0FDFA] border border-[#A5F3FC] rounded-2xl p-5" data-testid="qdus-exec-summary">
        <h3 className="font-bold text-[#0E7490] text-base mb-1">{exec.headline || 'Qualitative / Descriptor (DUS) Analysis'}</h3>
        <ul className="mt-2 space-y-1.5">
          {(exec.bullets || []).map((b, i) => (
            <li key={i} className="text-[13px] text-[#155E63] flex gap-2 leading-relaxed">
              <span className="text-[#06B6D4] mt-0.5">▸</span><span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          {exec.recommended_dus_descriptors?.length > 0 && (
            <div className="bg-white/70 rounded-lg p-3" data-testid="qdus-recommended">
              <div className="text-[10px] uppercase tracking-wider text-[#0E7490] font-bold mb-1">Recommended DUS descriptors</div>
              <div className="flex flex-wrap gap-1.5">
                {exec.recommended_dus_descriptors.map((t, i) => (
                  <span key={i} className="text-[11px] bg-[#CFFAFE] text-[#0E7490] rounded-full px-2 py-0.5 font-medium">{t}</span>
                ))}
              </div>
            </div>
          )}
          {exec.descriptors_to_drop?.length > 0 && (
            <div className="bg-white/70 rounded-lg p-3" data-testid="qdus-drop">
              <div className="text-[10px] uppercase tracking-wider text-[#B91C1C] font-bold mb-1">Drop for DUS (monomorphic)</div>
              <div className="flex flex-wrap gap-1.5">
                {exec.descriptors_to_drop.map((t, i) => (
                  <span key={i} className="text-[11px] bg-[#FEE2E2] text-[#991B1B] rounded-full px-2 py-0.5 font-medium">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Design summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="qdus-design-summary">
        {[
          ['Genotypes', ds.n_genotypes],
          ['Descriptors', ds.n_traits],
          ['Polymorphic', ds.n_polymorphic],
          ['Monomorphic', ds.n_monomorphic],
        ].map(([k, v]) => (
          <div key={k} className="bg-white border border-[#E8E6E1] rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-[#0E7490] font-mono">{v ?? '-'}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">{k}</div>
          </div>
        ))}
      </div>

      {/* Diversity summary table */}
      <div className="overflow-x-auto" data-testid="qdus-diversity-table">
        <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Per-Trait Diversity Summary</h4>
        <table className="w-full text-xs border-collapse">
          <thead><tr className="bg-[#0891B2] text-white">
            <th className="px-3 py-2 text-left">Descriptor</th>
            <th className="px-3 py-2 text-center">States</th>
            <th className="px-3 py-2 text-center">Shannon H′</th>
            <th className="px-3 py-2 text-center">H′max</th>
            <th className="px-3 py-2 text-center">Evenness J′</th>
            <th className="px-3 py-2 text-center">Simpson D</th>
            <th className="px-3 py-2 text-center">Missing %</th>
            <th className="px-3 py-2 text-center">Class</th>
          </tr></thead>
          <tbody>
            {diversity.map((t, i) => {
              const st = _QDUS_CLASS_STYLE[t.classification] || _QDUS_CLASS_STYLE.polymorphic;
              return (
                <tr key={t.trait} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FBFC]'}>
                  <td className="px-3 py-1.5 font-medium">{t.trait}</td>
                  <td className="px-3 py-1.5 text-center">{t.n_states}</td>
                  <td className="px-3 py-1.5 text-center font-mono font-bold">{t.shannon_H}</td>
                  <td className="px-3 py-1.5 text-center font-mono text-[#8A8580]">{t.shannon_Hmax}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{t.evenness ?? '—'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{t.simpson_D}</td>
                  <td className="px-3 py-1.5 text-center">{t.missing_percent}%</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}` }}>{st.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per-trait frequency cards */}
      <div>
        <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Frequency Distribution & Interpretation</h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {traits.map((t, i) => {
            const st = _QDUS_CLASS_STYLE[t.classification] || _QDUS_CLASS_STYLE.polymorphic;
            return (
              <div key={t.trait} className="bg-white border border-[#E8E6E1] rounded-xl overflow-hidden" data-testid={`qdus-trait-${i}`}>
                <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8E6E1]" style={{ background: st.bg }}>
                  <span className="font-semibold text-sm" style={{ color: st.text }}>{t.trait}</span>
                  <span className="text-[10px] font-mono" style={{ color: st.text }}>H′={t.shannon_H} · D={t.simpson_D}</span>
                </div>
                {t.bar_chart_png && (
                  <img src={`data:image/png;base64,${t.bar_chart_png}`} alt={`${t.trait} frequency`}
                    className="w-full object-contain" style={{ maxHeight: 240 }} data-testid={`qdus-bar-${i}`} />
                )}
                <div className="px-4 py-2 text-[12px] text-[#4B4945] leading-relaxed border-t border-[#F0EEE9]">{t.interpretation}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Gower dendrogram + clusters */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="qdus-gower">
        <div className="bg-[#F0FBFC] px-4 py-2 border-b border-[#E8E6E1] flex items-center justify-between flex-wrap gap-2">
          <h4 className="font-semibold text-sm text-[#0E7490] uppercase tracking-wider">Gower Dissimilarity Clustering (UPGMA)</h4>
          <span className="text-[11px] text-[#6E6B66]">
            {gower.n_clusters} morphotype cluster(s) · mean dissimilarity {gower.mean_dissimilarity}
          </span>
        </div>
        {gower.dendrogram_png && (
          <img src={`data:image/png;base64,${gower.dendrogram_png}`} alt="Gower UPGMA dendrogram"
            className="w-full object-contain p-2" data-testid="qdus-dendrogram-img" />
        )}
        <div className="px-4 py-3 border-t border-[#F0EEE9]">
          <div className="text-[10px] uppercase tracking-wider text-[#8A8580] font-semibold mb-2">Morphotype clusters</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(gower.clusters || []).map((c) => (
              <div key={c.cluster} className="bg-[#F0FBFC] rounded-lg p-2 text-[12px]">
                <span className="font-bold text-[#0E7490]">Cluster {c.cluster}</span>
                <span className="text-[#8A8580]"> ({c.n}): </span>
                <span className="text-[#4B4945]">{(c.genotypes || []).join(', ')}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#8A8580] italic mt-2">
            Gower distance (simple-matching for nominal descriptors) is used — Euclidean/Pearson distances are invalid on categorical codes.
          </p>
        </div>
      </div>

      {/* DUS distinctness */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="qdus-distinctness">
        <div className="bg-[#F4FAF5] px-4 py-2 border-b border-[#E8E6E1]">
          <h4 className="font-semibold text-sm text-[#1F4E2E] uppercase tracking-wider">DUS Distinctness Matrix</h4>
        </div>
        <div className="p-4">
          <div className={`text-[13px] px-3 py-2 rounded-md mb-3 ${dist.n_indistinguishable ? 'bg-[#FEF2F2] text-[#991B1B] border-l-4 border-[#DC2626]' : 'bg-[#F0FDF4] text-[#166534] border-l-4 border-[#16A34A]'}`} data-testid="qdus-dus-verdict">
            {dist.dus_verdict}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px] mb-3">
            {[
              ['Total pairs', dist.n_pairs],
              ['Min differing', dist.min_differing],
              ['Mean differing', dist.mean_differing],
              ['Max differing', dist.max_differing],
            ].map(([k, v]) => (
              <div key={k} className="text-center">
                <div className="font-mono font-bold text-[#2C2A28]">{v}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8580]">{k}</div>
              </div>
            ))}
          </div>
          {dist.indistinguishable_pairs?.length > 0 && (
            <div className="mb-3" data-testid="qdus-indistinguishable">
              <div className="text-[11px] font-semibold text-[#B91C1C] uppercase tracking-wider mb-1">Indistinguishable pairs (0 differing descriptors)</div>
              <div className="flex flex-wrap gap-1.5">
                {dist.indistinguishable_pairs.map((p, i) => (
                  <span key={i} className="text-[11px] bg-[#FEE2E2] text-[#991B1B] rounded px-2 py-0.5">{p.pair}</span>
                ))}
              </div>
            </div>
          )}
          {dist.minimally_distinct_pairs?.length > 0 && (
            <div data-testid="qdus-minimally-distinct">
              <div className="text-[11px] font-semibold text-[#92400E] uppercase tracking-wider mb-1">Minimally-distinct pairs (differ in exactly 1 descriptor)</div>
              <div className="space-y-1">
                {dist.minimally_distinct_pairs.slice(0, 12).map((p, i) => (
                  <div key={i} className="text-[11px] text-[#78350F]">
                    <span className="font-medium">{p.pair}</span> — differ only in <span className="font-mono">{(p.differing_traits || []).join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MCA biplot */}
      {data.mca?.available && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="qdus-mca">
          <div className="bg-[#F0FBFC] px-4 py-2 border-b border-[#E8E6E1] flex items-center justify-between flex-wrap gap-2">
            <h4 className="font-semibold text-sm text-[#0E7490] uppercase tracking-wider">Multiple Correspondence Analysis (MCA)</h4>
            <span className="text-[11px] text-[#6E6B66]">{data.mca.note}</span>
          </div>
          {data.mca.biplot_png && (
            <img src={`data:image/png;base64,${data.mca.biplot_png}`} alt="MCA biplot"
              className="w-full object-contain p-2" style={{ maxHeight: 460 }} data-testid="qdus-mca-biplot" />
          )}
          <p className="px-4 pb-3 text-[11px] text-[#8A8580] italic">
            Genotypes (●) close together share similar descriptor profiles; nearby descriptor states (▲) characterise that group. Axis inertia %: {(data.mca.inertia_pct || []).join(', ')}.
          </p>
        </div>
      )}
      {data.mca && !data.mca.available && (
        <div className="bg-[#F9FAFB] border border-[#E8E6E1] rounded-lg px-4 py-3 text-[12px] text-[#6E6B66]" data-testid="qdus-mca-note">
          <span className="font-semibold">MCA:</span> {data.mca.note}
        </div>
      )}

      {/* Nominal trait associations (Chi-square / Fisher / Cramér's V) */}
      {data.associations?.pairs?.length > 0 && (
        <div className="overflow-x-auto" data-testid="qdus-associations">
          <h4 className="font-semibold text-[#2C2A28] mb-1 text-sm uppercase tracking-wider">Nominal Trait Associations (χ² / Fisher)</h4>
          <p className="text-[11px] text-[#6E6B66] mb-2">{data.associations.note} Showing top {Math.min(20, data.associations.pairs.length)} by p-value.</p>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#7C3AED] text-white">
              <th className="px-3 py-2 text-left">Descriptor A</th>
              <th className="px-3 py-2 text-left">Descriptor B</th>
              <th className="px-3 py-2 text-center">χ²</th>
              <th className="px-3 py-2 text-center">df</th>
              <th className="px-3 py-2 text-center">p-value</th>
              <th className="px-3 py-2 text-center">Cramér's V</th>
              <th className="px-3 py-2 text-center">Test</th>
            </tr></thead>
            <tbody>
              {data.associations.pairs.slice(0, 20).map((p, i) => (
                <tr key={i} className={p.significant ? 'bg-[#F5F3FF]' : 'bg-white'}>
                  <td className="px-3 py-1.5 font-medium">{p.trait_a}</td>
                  <td className="px-3 py-1.5 font-medium">{p.trait_b}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.chi2}</td>
                  <td className="px-3 py-1.5 text-center">{p.dof}</td>
                  <td className="px-3 py-1.5 text-center font-mono font-bold">
                    {p.p_value < 0.001 ? '<0.001' : p.p_value}
                    {p.significant && <span className="text-[#7C3AED]"> *</span>}
                  </td>
                  <td className="px-3 py-1.5 text-center font-mono">{p.cramers_v}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`text-[10px] ${p.small_cell ? 'text-[#B45309]' : 'text-[#6E6B66]'}`}>
                      {p.test_used}{p.small_cell && p.test_used === 'chi-square' ? ' ⚠' : ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-[#8A8580] mt-1">⚠ = expected cell &lt; 5 (χ² unreliable). * = significant at p &lt; 0.05. Cramér's V is the effect size (0–1).</p>
        </div>
      )}
      {data.associations && data.associations.pairs?.length === 0 && (
        <div className="bg-[#F9FAFB] border border-[#E8E6E1] rounded-lg px-4 py-3 text-[12px] text-[#6E6B66]" data-testid="qdus-associations-note">
          <span className="font-semibold">Nominal trait associations (χ² / Fisher):</span> {data.associations.note || 'Needs at least 2 polymorphic nominal descriptors to test associations.'}
        </div>
      )}

      {/* Ordinal trait associations (Kendall's tau) */}
      {data.ordinal_associations && (
        <div data-testid="qdus-ordinal-assoc">
          <h4 className="font-semibold text-[#2C2A28] mb-1 text-sm uppercase tracking-wider">Ordinal Trait Associations (Kendall's τ)</h4>
          {data.ordinal_associations.pairs?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-[#0D9488] text-white">
                  <th className="px-3 py-2 text-left">Descriptor A</th>
                  <th className="px-3 py-2 text-left">Descriptor B</th>
                  <th className="px-3 py-2 text-center">n</th>
                  <th className="px-3 py-2 text-center">Kendall τ</th>
                  <th className="px-3 py-2 text-center">p-value</th>
                </tr></thead>
                <tbody>
                  {data.ordinal_associations.pairs.map((p, i) => (
                    <tr key={i} className={p.significant ? 'bg-[#F0FDFA]' : 'bg-white'}>
                      <td className="px-3 py-1.5 font-medium">{p.trait_a}</td>
                      <td className="px-3 py-1.5 font-medium">{p.trait_b}</td>
                      <td className="px-3 py-1.5 text-center">{p.n}</td>
                      <td className="px-3 py-1.5 text-center font-mono font-bold">{p.tau}</td>
                      <td className="px-3 py-1.5 text-center font-mono">{p.p_value === null ? '—' : (p.p_value < 0.001 ? '<0.001' : p.p_value)}{p.significant && <span className="text-[#0D9488]"> *</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[12px] text-[#6E6B66] italic" data-testid="qdus-ordinal-note">{data.ordinal_associations.note}</p>
          )}
        </div>
      )}

      {/* Uniformity / off-type */}
      {data.uniformity?.enabled ? (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden" data-testid="qdus-uniformity">
          <div className="bg-[#FFFBEB] px-4 py-2 border-b border-[#E8E6E1] flex items-center justify-between flex-wrap gap-2">
            <h4 className="font-semibold text-sm text-[#92400E] uppercase tracking-wider">Uniformity / Off-Type Check</h4>
            <span className="text-[11px] text-[#78350F]">{data.uniformity.n_uniform}/{data.uniformity.n_accessions} accessions uniform (≤{data.uniformity.threshold_pct}% off-types)</span>
          </div>
          <div className="p-4">
            <p className="text-[11px] text-[#6E6B66] mb-3">{data.uniformity.note}</p>
            <div className="space-y-2">
              {(data.uniformity.per_accession || []).map((a, i) => {
                const offs = (a.traits || []).filter(t => t.off_types > 0);
                return (
                  <div key={i} className={`rounded-lg p-2 text-[12px] border ${a.uniform ? 'border-[#BBF7D0] bg-[#F0FDF4]' : 'border-[#FDE68A] bg-[#FFFBEB]'}`}>
                    <span className="font-bold" style={{ color: a.uniform ? '#166534' : '#92400E' }}>{a.genotype}</span>
                    <span className="text-[#8A8580]"> ({a.n_plants} plants) — {a.uniform ? 'UNIFORM' : 'off-types present'}</span>
                    {offs.length > 0 && (
                      <span className="text-[#78350F]">: {offs.map(t => `${t.trait} ${t.off_types}/${t.n_scored} (${t.off_type_pct}%)`).join(', ')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (data.uniformity && (
        <div className="bg-[#F9FAFB] border border-[#E8E6E1] rounded-lg px-4 py-3 text-[12px] text-[#6E6B66]" data-testid="qdus-uniformity-note">
          <span className="font-semibold">Uniformity:</span> {data.uniformity.note}
        </div>
      ))}

      {/* Verification checklist */}
      {data.verification_checklist?.length > 0 && (
        <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-2xl p-4" data-testid="qdus-checklist">
          <h4 className="font-semibold text-sm text-[#2C2A28] uppercase tracking-wider mb-2">Cross-Verification Checklist</h4>
          <p className="text-[11px] text-[#8A8580] mb-2">Audit the written interpretation against the computed tables:</p>
          <ul className="space-y-1.5">
            {data.verification_checklist.map((c, i) => (
              <li key={i} className="text-[12px] text-[#4B4945] flex gap-2 leading-relaxed">
                <span className="text-[#16A34A] mt-0.5">✓</span><span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {data.warnings?.length > 0 && (
        <div className="bg-[#FFFBEB] border-l-4 border-[#D97706] rounded-md p-4" data-testid="qdus-warnings">
          <div className="font-bold text-[#92400E] text-[11px] uppercase tracking-wider mb-1">Statistical caveats</div>
          <ul className="space-y-1">
            {data.warnings.map((w, i) => (
              <li key={i} className="text-[12px] text-[#78350F] leading-relaxed">• {w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Method notes + citations */}
      <div className="bg-[#FBFAF7] border border-[#E8E6E1] rounded-lg px-4 py-3" data-testid="qdus-method-notes">
        <div className="text-[10px] uppercase tracking-wider text-[#8A8580] font-semibold mb-1">Methods & Validity Notes</div>
        <ul className="space-y-1 mb-2">
          {(data.method_notes || []).map((m, i) => (
            <li key={i} className="text-[11px] text-[#6E6B66] leading-relaxed">• {m}</li>
          ))}
        </ul>
        <div className="text-[10px] uppercase tracking-wider text-[#8A8580] font-semibold mb-1 mt-2">References</div>
        <ul className="space-y-0.5">
          {(data.citations || []).map((c, i) => (
            <li key={i} className="text-[10px] text-[#8A8580] leading-relaxed">{c}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
